import {
  QuizInputTemplate,
  QuizOutputTemplate,
} from '@efdigital/ll-addon-content/components/quiz/types';
import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import {
  type Cohort,
  DataTypeQuiz,
  type Quiz,
  type QuizQuestion,
  type QuizQuestionChoiceOption,
} from '@efdigital/ll-addon-data';
import { generateRandomSlug } from '@efdigital/ll-addon-data';
import { SingleChoiceQuestionManager } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/managers/choice-single';
import { DEFAULT_RESPONSE } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/types/quiz-responses';
import { type OptionWithValue } from '@efdigital/ll-admin-v2/utils/quiz-choices';
import type { Store } from '@warp-drive/core';
import { Type } from '@warp-drive/core/types/symbols';

export async function createCohortSurvey(
  cohort: Cohort,
  context: { store: Store; picklists: PicklistsService }
): Promise<Quiz> {
  const program = await cohort.program;

  const survey = context.store.createRecord<Quiz>(DataTypeQuiz.Quiz, {
    title: `${program?.title} - ${cohort.title} - Survey`,
    language: 'en',
    markdown:
      'Your honest feedback is important to help us improve our programs and service for you and future participants. Thank you for taking the time to complete this questionnaire.\n\n* The survey will take 10 minutes to complete.',
    templateInput: QuizInputTemplate.Paged,
    templateOutput: QuizOutputTemplate.Certificate,
    templatePdf: 'program-quiz-3a',
    scoringMethod: {
      [Type]: DataTypeQuiz.QuizScoringMethod,
      type: 'default',
      qid: '',
      hideZero: false,
      sort: 'none',
    },
    minSelections: 0,
    maxSelections: 0,
    needsApproval: false,
    needsAssignment: false,
    reportAnswers: true,
    reportAnswersInternal: false,
    preventEdits: true,
    category1: 'Surveys',
    category2: 'Custom',
    category3: cohort.region,
    category4: cohort.deliveryType,
    category5: '',
    questions: [],
    totals: [],
    categories: [],
    comparisons: [],
    systemLineage: 'll-admin-v2',
  });

  survey.questions.push(createQuestionNPS());

  return survey;
}

export function createQuestionNPS(): QuizQuestion {
  const question: QuizQuestion = {
    [Type]: DataTypeQuiz.QuizQuestion,
    type: SingleChoiceQuestionManager.type,
    qid: generateRandomSlug(),
    title:
      'On a scale from 0-10, how likely are you to recommend Hult Ashridge to a colleague?',
    markdown: '',
    css: '',
    crm: '',
    required: true,
    preview: true,
    ...structuredClone(SingleChoiceQuestionManager.defaultData),
    reporting: {
      [Type]: DataTypeQuiz.QuizReportingQuestion,
      groupName: '',
      questionName: '',
    },
  };

  for (let i = 0; i <= 10; i++) {
    question.choice!.choices.push(
      createSingleChoice({ value: i.toString(), score: i })
    );
  }

  return question;
}

function createSingleChoice(
  option?: OptionWithValue
): QuizQuestionChoiceOption {
  return {
    [Type]: DataTypeQuiz.QuizQuestionChoiceOption,
    oid: generateRandomSlug(),
    value: option?.value ?? '',
    score: option?.score ?? 0,
    category: '',
    additionalCategories: [],
    correct: false,
    response: DEFAULT_RESPONSE,
  };
}
